import { CircleAlert, Clock } from "lucide-react";
import { Link } from "react-router";

import { useSession } from "@/auth/session";
import { cx } from "@/components/ui";
import { useI18n } from "@/i18n/provider";
import { formatDate } from "@/utils/dates";
import { usePlan } from "./plan";

/**
 * The plan's notice, under the top bar of every page (the phone shows it on
 * Home and Pharmacy account), only when there is something to say:
 *
 *   ending soon    amber   owners only, the last 7 days
 *   ended (grace)  red     owners; staff are asked to tell the owner
 *   view only      red     everybody
 *
 * Owners get See plans, to Billing (the phone cannot: Google Play's rules).
 * Paying makes it go at the next check, within a minute once the plan has ended.
 */
export function PlanNoticeBar() {
  const { t } = useI18n();
  const { state } = useSession();
  const plan = usePlan();
  const status = plan.status;
  if (!plan.notice || !status) return null;

  const isOwner = state.status === "signedIn" && state.me.role === "admin";
  const day = (iso: string | null) => (iso ? formatDate(iso) : "");
  const [title, body] =
    plan.notice === "ending"
      ? [t.planEndingTitle, t.planEndingBody(status.daysLeft ?? 0, day(status.paidThrough))]
      : plan.notice === "grace"
        ? [
            t.planEndedTitle,
            isOwner ? t.planGraceOwner(day(status.paidThrough), day(status.graceUntil)) : t.planGraceStaff,
          ]
        : [t.viewOnly, isOwner ? t.viewOnlyOwner(day(status.readOnlyFrom)) : t.viewOnlyStaff];
  const warn = plan.notice === "ending";
  const Icon = warn ? Clock : CircleAlert;

  return (
    <div
      role="alert"
      className={cx(
        "flex items-start gap-3 border-b px-4 py-3 md:px-6 lg:px-10",
        warn ? "border-warn bg-warn-surface" : "border-danger bg-danger-surface",
      )}
    >
      <Icon size={22} aria-hidden="true" className={cx("mt-0.5 shrink-0", warn ? "text-warn" : "text-danger")} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cx("font-bold", warn ? "text-warn" : "text-danger")}>{title}</span>
        <span className="text-label text-body">{body}</span>
      </div>
      {isOwner && (
        <Link
          to="/billing"
          className={cx(
            "shrink-0 self-center rounded-control border px-3 py-1.5 text-label font-semibold",
            warn ? "border-warn text-warn hover:bg-warn/10" : "border-danger text-danger hover:bg-danger/10",
          )}
        >
          {t.seePlans}
        </Link>
      )}
    </div>
  );
}
